import type { Middleware } from '@reduxjs/toolkit';
import { logAnalyticsEvent, AnalyticsEventMetadata } from './analytics';

/**
 * Mapping from a Redux action type to a Statsig event. Apps supply these
 * to createAnalyticsMiddleware to wire up event logging for their own
 * Redux thunks / slices.
 *
 * Action types follow the RTK convention `<slice>/<action>/fulfilled`.
 */
export interface ReduxEventMapping {
  /** Full action type, e.g. "contexts/createContextThunk/fulfilled". */
  type: string;
  /** Statsig event name to log. */
  event: string;
  /** Extract metadata from action.payload (the resolved thunk return value). */
  extract?: (payload: unknown, meta: unknown) => AnalyticsEventMetadata;
  /** Optional value (often resource id) — Statsig second arg. */
  value?: (payload: unknown, meta: unknown) => string | number | undefined;
}

/**
 * Factory: returns a Redux middleware that logs analytics events for
 * matched action types. Pass an empty array to get a no-op middleware
 * (useful when an app doesn't have Redux thunks worth instrumenting).
 */
export function createAnalyticsMiddleware(
  mappings: ReduxEventMapping[]
): Middleware {
  const byType = new Map<string, ReduxEventMapping>();
  for (const m of mappings) byType.set(m.type, m);

  const middleware: Middleware = () => (next) => (action) => {
    const result = next(action);
    const typedAction = action as { type?: unknown; payload?: unknown; meta?: unknown };
    if (!typedAction || typeof typedAction.type !== 'string') return result;

    const mapping = byType.get(typedAction.type);
    if (!mapping) return result;

    try {
      logAnalyticsEvent(
        mapping.event,
        mapping.value?.(typedAction.payload, typedAction.meta),
        mapping.extract?.(typedAction.payload, typedAction.meta)
      );
    } catch {
      // Never let analytics break Redux flow.
    }
    return result;
  };

  return middleware;
}
